import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, GitCompareArrows, Info, Layers, ListChecks, Pause, Play, Search, Tag, Timer, Copy, Eye, EyeOff, AlertTriangle, ShieldAlert } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';

const PulumiIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [exampleStack, setExampleStack] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [organization, setOrganization] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [connectApiToken, setConnectApiToken] = useState('');
  const [connectOrganization, setConnectOrganization] = useState('');
  const [connectBaseUrl, setConnectBaseUrl] = useState('');

  const webhookUrl = `${window.location.origin}/api/webhooks/pulumi`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getPulumiStatus();
      if (data?.connected) {
        setConnected(true);
        setOrganization(data.organization || '');
        setBaseUrl(data.base_url || '');
        setWebhookSecret(data.webhook_secret || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectPulumi({
        api_token: connectApiToken,
        organization: connectOrganization.trim(),
        base_url: connectBaseUrl.trim() || undefined,
      });
      setSuccessMsg('Pulumi Cloud connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectPulumi();
      setConnected(false);
      setSuccessMsg('Pulumi Cloud disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testPulumi();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Pulumi Cloud (${data.stack_count ?? 0} stack${data.stack_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyValue = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={pulumiIcon} alt="Pulumi Cloud" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Pulumi Cloud
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Run deployments (update, preview, refresh, destroy), detect and remediate drift, read stack outputs, manage stack tags, react to update and deployment failures, and run AI investigations against Pulumi Cloud</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {organization && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium truncate">{organization}</p>
                </div>
              )}
              {baseUrl && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">API URL</span>
                  <p className="font-medium truncate">{baseUrl}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Webhook Events Received</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Webhooks</CardTitle>
            <CardDescription>Add an organization or per-stack webhook in Pulumi Cloud to trigger workflows on stack, update, deployment, drift, and policy events. Actions work without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Payload URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copyValue('url', webhookUrl)} className="gap-1 shrink-0">
                  {copied === 'url' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'url' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            {webhookSecret && (
              <div>
                <Label className="text-sm">Webhook Secret</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{showSecret ? webhookSecret : '••••••••••••••••••••••••'}</code>
                  <Button variant="outline" size="sm" onClick={() => setShowSecret(s => !s)} className="gap-1 shrink-0" aria-label={showSecret ? 'Hide secret' : 'Show secret'}>
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyValue('secret', webhookSecret)} className="gap-1 shrink-0">
                    {copied === 'secret' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === 'secret' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              In Pulumi Cloud, open your organization's <strong className="text-foreground">Settings → Integrations</strong>, scroll to the <strong className="text-foreground">Webhooks</strong> section, and click <strong className="text-foreground">Add webhook</strong> (or use a stack's <strong className="text-foreground">Settings → Webhooks</strong> for per-stack events). Choose the <strong className="text-foreground">Webhook</strong> destination (not Slack or Microsoft Teams), give it a display name, set the <strong className="text-foreground">Payload URL</strong> to the URL above, and paste the secret into the <strong className="text-foreground">Secret</strong> field. Under <strong className="text-foreground">Triggers</strong>, check <strong className="text-foreground">All Stack events</strong>, <strong className="text-foreground">All Deployment events</strong> (this includes the drift detection and remediation events), <strong className="text-foreground">All Policy events</strong>, <strong className="text-foreground">All Environment events</strong>, and <strong className="text-foreground">All Change requests</strong> — Kestrel needs all of these event groups. Pulumi Cloud signs each delivery with HMAC-SHA256 (<code className="bg-muted px-1 rounded text-[11px] font-mono">Pulumi-Webhook-Signature</code> header) and Kestrel verifies it.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Pulumi Cloud actions and triggers in your workflows to automate deployment gating, drift response, and failure investigation.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (webhook)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Update Succeeded / Failed', desc: 'Triggers when a stack update, preview, or destroy finishes.', Icon: CheckCircle2 },
                  { name: 'Deployment Started / Succeeded / Failed', desc: 'Triggers on Pulumi Deployments lifecycle events.', Icon: Play },
                  { name: 'Drift Detected', desc: 'Triggers when a scheduled drift run detects infrastructure drift.', Icon: GitCompareArrows },
                  { name: 'Policy Violation', desc: 'Triggers when a mandatory policy pack blocks an update.', Icon: ShieldAlert },
                  { name: 'Stack Created / Deleted', desc: 'Triggers on stack lifecycle changes in the organization.', Icon: AlertTriangle },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Run Deployment', desc: 'Start an update, preview, refresh, destroy, or drift run on a stack via Pulumi Deployments.', Icon: Play },
                  { name: 'Wait for Deployment', desc: 'Poll a deployment until it reaches a terminal state and return the result.', Icon: Timer },
                  { name: 'Cancel Deployment', desc: 'Cancel an in-progress deployment on a stack.', Icon: ListChecks },
                  { name: 'Pause / Resume Deployments', desc: 'Pause or resume the deployment queue on a stack during incidents.', Icon: Pause },
                  { name: 'Get Stack Outputs', desc: 'Read the stack\u2019s current outputs for downstream steps.', Icon: Info },
                  { name: 'Check Drift', desc: 'Read the latest drift run results for a stack.', Icon: GitCompareArrows },
                  { name: 'Set / Delete Stack Tag', desc: 'Manage stack tags for ownership, environment, and incident markers.', Icon: Tag },
                  { name: 'Investigate Pulumi', desc: 'AI investigates stacks, updates, deployments, outputs, and drift (read-only).', Icon: Search },
                  { name: 'List Stacks / Updates / Deployments', desc: 'Enumerate org stacks, recent updates, and deployment history.', Icon: Layers },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a Pulumi Cloud update fails on the{' '}
              <input
                value={exampleStack}
                onChange={e => setExampleStack(e.target.value)}
                placeholder="project/stack"
                style={{ width: `${Math.max(exampleStack.length || 13, 13)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}stack, investigate the failure, post the findings to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack and ask for approval, then retry the update if approved.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleStack || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a Pulumi Cloud update fails on the ${exampleStack} stack, investigate the failure, post the findings to ${exampleChannel} on Slack and ask for approval, then retry the update if approved.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={pulumiIcon} alt="Pulumi Cloud" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Pulumi Cloud</CardTitle>
                <CardDescription>Connect Pulumi Cloud to run and gate deployments, read stack outputs, detect and remediate drift, react to update failures and policy violations, and run AI investigations in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="pulumi-token" className="text-sm">Access Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the Pulumi Cloud REST API with an <strong className="text-foreground">organization or personal access token</strong>. Create an organization token under <strong className="text-foreground">Organization Settings → Access Tokens</strong> (recommended), or a personal token under <strong className="text-foreground">Personal Settings → Access Tokens</strong>.
              </p>
              <Input
                id="pulumi-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="pul-..."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the Pulumi Cloud API.
              </p>
            </div>
            <div>
              <Label htmlFor="pulumi-org" className="text-sm">Organization</Label>
              <Input
                id="pulumi-org"
                value={connectOrganization}
                onChange={e => setConnectOrganization(e.target.value)}
                placeholder="my-org"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The Pulumi Cloud organization name, as shown in the URL: <code className="bg-muted px-1 rounded text-[11px] font-mono">app.pulumi.com/&lt;organization&gt;</code>.
              </p>
            </div>
            <div>
              <Label htmlFor="pulumi-base-url" className="text-sm">API URL (optional)</Label>
              <Input
                id="pulumi-base-url"
                value={connectBaseUrl}
                onChange={e => setConnectBaseUrl(e.target.value)}
                placeholder="https://api.pulumi.com"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty for Pulumi Cloud. For a self-hosted Pulumi instance, set its API base URL, e.g. <code className="bg-muted px-1 rounded text-[11px] font-mono">https://pulumi.example.com</code>.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectApiToken || !connectOrganization.trim()}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Pulumi Cloud
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default PulumiIntegration;
